import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Button,
  Chip,
  IconButton,
  InputBase,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import React, { useEffect, useRef } from 'react';

export interface ColumnSelectionHeaderProps {
  title: string;
  titleId: string;
  summaryId: string;
  query: string;
  onQueryChange: (query: string) => void;
  selectedOnly: boolean;
  onToggleSelectedOnly: () => void;
  totals: { selected: number; total: number };
  /** Rows currently on screen — what the global actions will act on. */
  visibleCount: number;
  isFiltered: boolean;
  onSelectVisible: (checked: boolean) => void;
}

export const ColumnSelectionHeader: React.FC<ColumnSelectionHeaderProps> = ({
  title,
  titleId,
  summaryId,
  query,
  onQueryChange,
  selectedOnly,
  onToggleSelectedOnly,
  totals,
  visibleCount,
  isFiltered,
  onSelectVisible,
}) => {
  const theme = useTheme();
  const searchRef = useRef<HTMLInputElement>(null);

  // Land the caret in the search box on open. The dialog unmounts its content
  // when closed, so this mount effect runs once per visit. Done with a ref
  // rather than `autoFocus`, which jsx-a11y forbids.
  useEffect(() => {
    searchRef.current?.focus();
  }, []);

  return (
    <Box
      sx={{
        px: 2,
        pt: 2,
        pb: 1.5,
        borderBottom: `1px solid ${theme.palette.v4.border}`,
      }}
    >
      <Stack direction="row" alignItems="baseline" spacing={1}>
        <Typography id={titleId} variant="subtitle1" sx={{ flex: 1, fontWeight: 600 }}>
          {title}
        </Typography>
        <Typography id={summaryId} variant="caption" sx={{ color: theme.palette.text.secondary }}>
          {totals.selected} of {totals.total} selected
        </Typography>
      </Stack>

      <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.75}
          sx={{
            flex: 1,
            border: `1px solid ${theme.palette.v4.filterInputBorder}`,
            borderRadius: 1,
            px: 1.5,
            py: 0.5,
          }}
        >
          <SearchIcon fontSize="small" sx={{ color: theme.palette.text.secondary }} />
          <InputBase
            fullWidth
            inputRef={searchRef}
            placeholder="Search columns…"
            value={query}
            onChange={(event) => onQueryChange(event.target.value)}
            onKeyDown={(event) => {
              // Clear the query first; only an already-empty box lets Escape
              // fall through to the dialog and close it.
              if (event.key === 'Escape' && query) {
                event.stopPropagation();
                onQueryChange('');
              }
            }}
            inputProps={{ 'aria-label': 'Search columns' }}
            sx={{ fontSize: '0.875rem' }}
          />
          {query && (
            <IconButton
              size="small"
              aria-label="Clear search"
              onClick={() => {
                onQueryChange('');
                searchRef.current?.focus();
              }}
              sx={{ p: 0.25 }}
            >
              <ClearIcon fontSize="small" sx={{ color: theme.palette.text.secondary }} />
            </IconButton>
          )}
        </Stack>

        <Chip
          size="small"
          clickable
          label={`Selected only (${totals.selected})`}
          onClick={onToggleSelectedOnly}
          variant={selectedOnly ? 'filled' : 'outlined'}
          aria-pressed={selectedOnly}
          sx={{
            borderColor: theme.palette.v4.filterInputBorder,
            ...(selectedOnly && {
              bgcolor: theme.palette.v4.accentDark,
              color: theme.palette.common.white,
              '&:hover': { bgcolor: theme.palette.v4.accentDark },
            }),
          }}
        />
      </Stack>

      <Stack
        direction="row"
        justifyContent="flex-end"
        alignItems="center"
        spacing={1}
        sx={{ mt: 0.5 }}
      >
        <Button
          size="small"
          disabled={visibleCount === 0}
          onClick={() => onSelectVisible(true)}
          sx={{ minWidth: 0, color: theme.palette.v4.accentDark, textTransform: 'none' }}
        >
          {/* "columns" distinguishes these from the per-group pair below, which
              otherwise render the same words a few pixels away. */}
          {isFiltered ? `Select ${visibleCount} shown columns` : 'Select all columns'}
        </Button>
        <Typography variant="caption" sx={{ color: theme.palette.v4.borderDark }}>
          |
        </Typography>
        <Button
          size="small"
          disabled={visibleCount === 0}
          onClick={() => onSelectVisible(false)}
          sx={{ minWidth: 0, color: theme.palette.v4.accentDark, textTransform: 'none' }}
        >
          {isFiltered ? `Clear ${visibleCount} shown columns` : 'Clear all columns'}
        </Button>
      </Stack>
    </Box>
  );
};
